import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { startCase } from 'lodash';
import type { FC } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form@latest';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { isDateGreaterThanToday } from '@/helpers';
import { _isEmpty, _isNil, _notNil } from '@/littledash';

import type { Animal, AnimalApiId, AnimalIdentifier, AnimalSex } from '@/model/Animal.model';
import { getSexOptions } from '@/model/Animal.utils';
import type { ISODate } from '@/model/Common.model';
import type { State } from '@/model/State.model';
import type { StudyApiId } from '@/model/Study.model';

import { featuresSelector } from '@/support/Selectors';
import { useRequest } from '@/support/Hooks/request';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

import { validAnimalNameLength } from '@/components/Colony/Animals/Animals.utils.tsx';
import DatePickerNative from '@/components/UI/DatePickerNative';
import Button from '@/components/UI/Button';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import Select from '@/components/UI/FormFields/Latest/Select';

import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';

import { checkStudyForDuplicates } from '../AssignIdentifiers/AssignIdentifiers.utils';

interface EditSubjectProps {
  animal: Animal;
  studyApiId: StudyApiId;
  handleCallback: (message: string) => void;
  closeModal: () => void;
}

interface AnimalUploadFormData {
  name: string;
  dob: ISODate;
  sex: AnimalSex;
  tail: string;
  tag: string;
  donor: string;
  ear: string;
}

interface AnimalUpdatePayload {
  name: string;
  dob: ISODate | null;
  sex: AnimalSex;
  alt_ids: {
    tail: string;
    tag: string;
    donor: string;
    ear: string;
  };
}

const idFields: Array<{ label: string; name: AnimalIdentifier; required?: boolean }> = [
  {
    label: 'Name',
    name: 'name',
    required: true,
  },
  {
    label: 'Tail',
    name: 'tail',
  },
  {
    label: 'Ear',
    name: 'ear',
  },
  {
    label: 'Tag',
    name: 'tag',
  },
  {
    label: 'Donor',
    name: 'donor',
  },
];

const EditSubject: FC<EditSubjectProps> = ({ animal, studyApiId, handleCallback, closeModal }) => {
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const isDobRequired = entityFieldRequiredMap?.dob ?? true;
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const sexOptions = getSexOptions(unknownSexEnabled);
  const sexDefault = sexOptions.some(({ value }) => value === animal.sex) ? animal.sex : ('' as AnimalSex);

  const formMethods = useForm<AnimalUploadFormData>({
    defaultValues: {
      name: animal.name,
      dob: animal.dob || '',
      sex: sexDefault,
      ...animal.alt_ids,
    },
  });
  const { handleSubmit, control } = formMethods;
  const { sendRequest: updateAnimal, requestSending: saving } = useRequest({
    params: { id: animal.id },
    route: 'animal',
    method: 'patch',
  });

  const handlePayload = (data: AnimalUploadFormData): AnimalUpdatePayload => {
    return {
      name: data.name,
      dob: _isEmpty(data.dob) ? null : data.dob,
      sex: data.sex,
      alt_ids: {
        tail: data.tail,
        tag: data.tag,
        donor: data.donor,
        ear: data.ear,
      },
    };
  };

  const onSubmit = async (data: AnimalUploadFormData) => {
    await updateAnimal(handlePayload(data));
    handleCallback('Successfully updated animal');
    closeModal();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`center mv3 pa4 bg-white br2 shadow-4`}
      style={{ maxWidth: 420 }}
    >
      <div className="tc pb3">
        <h3 className="normal f4 lh-title">Edit {animal.name}</h3>
      </div>
      <FormProvider {...formMethods}>
        {idFields.map(({ name, label, required }) => (
          <EditSubjectNameField
            key={`${name}-field`}
            name={name}
            label={label}
            studyApiId={studyApiId}
            animalId={animal?.api_id}
            required={required}
          />
        ))}
        <div className="mb3">
          <FormField name="dob" label="Date of birth" required={isDobRequired}>
            <Controller
              name="dob"
              control={control}
              defaultValue={animal.dob || ''}
              rules={{
                validate: (value: string) => {
                  if (_isEmpty(value)) {
                    return !isDobRequired || 'DOB is required';
                  }
                  const selectedDate = new Date(value);
                  return !isDateGreaterThanToday(selectedDate) || 'DOB cannot be in the future';
                },
              }}
              render={({ field: { onChange, value } }) => (
                <DatePickerNative value={value || ''} onChange={onChange} testId="edit-subject-dob-input" />
              )}
            />
          </FormField>
        </div>
        <div className="mb3">
          <Select name="sex" label="Sex" required value={sexDefault} options={sexOptions} testId="edit-subject-sex" />
        </div>
      </FormProvider>
      <div className="pt2">
        <Button submit className="mr3" disabled={saving} testId="edit-subject__save">
          Save
        </Button>
        <Button plain onClick={closeModal}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

interface EditSubjectNameFieldProps {
  name: AnimalIdentifier;
  label: string;
  studyApiId: StudyApiId;
  animalId?: AnimalApiId;
  required?: boolean;
}

const EditSubjectNameField: FC<EditSubjectNameFieldProps> = ({
  name,
  label,
  studyApiId,
  animalId,
  required = false,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<AnimalUploadFormData>();
  const state = useSelector((state: State) => state?.team);
  const rules: { validate?: (value: string) => Promise<boolean | string> } = {};

  if (_isNil(animalId) || _isNil(studyApiId)) {
    return null;
  }

  if (name === 'tag' && state?.features?.assign_identifiers_tag_unique === true) {
    rules.validate = async (value) => {
      try {
        const result = await checkStudyForDuplicates(value, 'tag', studyApiId, animalId);
        return result ? true : 'This identifier is already in use';
      } catch (error) {
        return 'An error occurred';
      }
    };
  } else {
    rules.validate = async (value: string) => {
      if (required && _isEmpty(value)) {
        return `${startCase(name)} cannot be empty`;
      }
      if (name === 'name' && !validAnimalNameLength(value)) {
        return 'Name cannot be longer than 40 characters.';
      }
      return true;
    };
  }

  return (
    <div className="mb3">
      <label htmlFor={name}>{`${label}${required ? ' (required)' : ''}`}</label>
      <input
        id={name}
        data-testid={`edit-subject-${name}-input`}
        type="text"
        className={cn({ input__error: _notNil(errors[name]) })}
        {...register(name, rules)}
      />
      <ErrorMessage errors={errors} name={name} render={({ message }) => <p className="f6 red lh-copy">{message}</p>} />
    </div>
  );
};

export default EditSubject;
